/**
 * Merchant settings hooks (dispatch-mode + open-override).
 *
 * ADM-22: os dois hooks batiam em verbo/rota que não existem no backend, e o `as never` escondia
 * isso do compilador. Contrato real (MerchantController):
 *   - `PATCH /api/v1/merchants/{id}/dispatch-mode` com `{ mode: OWN_COURIERS | GLOBAL_POOL }`
 *     (era PUT → 405, com enum PLATFORM/OWN/HYBRID → 400);
 *   - o override de abertura é setado por `PUT /api/v1/merchants/{id}` com `isOpenOverride`
 *     (update parcial — todo campo é `if (x != null)`); a rota `/{id}/open-override` só tem
 *     DELETE, que LIMPA o override.
 *
 * O cast em dispatch-mode continua porque a rota ainda não está no `api.d.ts` gerado — o gerado
 * está defasado em relação ao backend, não o contrário. O de open-override caiu: `isOpenOverride`
 * já é tipado. Regenerar o OpenAPI (`make web-gen-api`) elimina o que sobrou.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import { merchantOpsKeys } from './keys';

/** Espelha `com.favelafood.api.modules.merchant.entity.DispatchMode`. */
export interface DispatchModeRequest {
  mode: 'OWN_COURIERS' | 'GLOBAL_POOL';
}

export function useUpdateDispatchMode(): UseMutationResult<
  void,
  Error,
  { merchantId: string; mode: DispatchModeRequest['mode'] }
> {
  const qc = useQueryClient();
  return useMutation<void, Error, { merchantId: string; mode: DispatchModeRequest['mode'] }>({
    mutationFn: async ({ merchantId, mode }) => {
      const { error } = await api.PATCH(
        '/api/v1/merchants/{id}/dispatch-mode' as never,
        { params: { path: { id: merchantId } }, body: { mode } } as never,
      );
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}

/**
 * Só `isOpen`: o override do backend é um booleano puro. O fluxo que aceita motivo e prazo é o
 * fechamento justificado (`POST /{id}/closure`, REQ-merchant-closure) — outro endpoint, outra tela.
 */
export interface OpenOverrideRequest {
  isOpen: boolean;
}

export function useUpdateOpenOverride(): UseMutationResult<
  void,
  Error,
  { merchantId: string; payload: OpenOverrideRequest }
> {
  const qc = useQueryClient();
  return useMutation<void, Error, { merchantId: string; payload: OpenOverrideRequest }>({
    mutationFn: async ({ merchantId, payload }) => {
      const { error } = await api.PUT('/api/v1/merchants/{id}', {
        params: { path: { id: merchantId } },
        body: { isOpenOverride: payload.isOpen },
      });
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}
